import type {
  BasicData,
  FluidFrictionPrerequisites,
  FluidProperties
} from "../d";
import { GRAVITY_FT_SEC } from "../../constants";
import { CubicFeetPerSecond } from "@/libs/units/flowRate";

export function getFluidFrictionPrerequisites(
  basicData: BasicData,
  fluidProperties: FluidProperties,
  pipeDiameterIn: number,
  pumpIntakePressurePSIA: number
): FluidFrictionPrerequisites {
  const {
    liquidSuperficialVelocityFTSEC,
    mixSuperficialVelocityFTSEC,
    gasSuperficialVelocityFTSEC,
    liquidDensityLBMFT3,
    gasDensityLBMFT3,
    liquidFlowRateBBLD,
    freeGasFlowRateRCFD,
    fluidFlowRateBBLD,
    oilSpecificGravity,
    waterCutRatio,
    liquidViscosityCP,
    gasLiquidRatioSCFSTB
  } = fluidProperties;

  const {
    surfaceTemperatureF,
    bottomholeTemperatureF,
    waterSpecificGravity,
    gasSpecificGravity
  } = basicData;
  const T = (surfaceTemperatureF + bottomholeTemperatureF) / 2;

  const P = pumpIntakePressurePSIA;

  // D188: =D51*350.52*(1/(1+D16))+D20*350.52*(D16/(1+D16))+D22*0.0764*D42
  const totalMass =
    oilSpecificGravity * 350.52 * (1 / (1 + waterCutRatio)) +
    waterSpecificGravity * 350.52 * (waterCutRatio / (1 + waterCutRatio)) +
    gasSpecificGravity * 0.0764 * gasLiquidRatioSCFSTB;

  // ONLY USED IN ORK AND BEGGS/BRILL
  // Calc updates from Friction Calcs Updated_10-31_PVT_Interfacial Tension.xlsx
  // D155: =39-0.2571*D21
  const o68 = 39 - 0.2571 * oilSpecificGravity;
  // D156: =37.5-0.2571*D21
  const o100 = 37.5 - 0.2571 * oilSpecificGravity;
  // D165: =(D163-((D12-68)*(D164-D163))/(100-68))
  const o68_100 = o68 - ((T - 68) * (o100 - o68)) / (100 - 68);
  // D167: =IF(D12<68,D163,IF(D12>100,D164,D165))
  let o_od = o68_100;
  if (T < 68) {
    o_od = o68;
  } else if (T > 100) {
    o_od = o100;
  }
  // D169: =EXP(-8.6306*10^-4*D10)
  const o_o = Math.exp(-8.6306e-4 * P);

  // D170: =D169*D167
  const gasOilInterfacialTension = o_o * o_od;

  // waterInterfacialTension - THIS CALC SHOULD BE VERIFIED - P === DATUM-PRESSURE?
  // D165: =(1.42157E-021 * (D35 + 14.7) ^ 6 - 4.32881E-017 * (D35 + 14.7) ^ 5 + 0.000000000000475377 * (D35 + 14.7) ^ 4 - 0.00000000221001 * (D35 + 14.7) ^ 3 + 0.00000423447 * (D35 + 14.7) ^ 2 - 0.00870071 * (D35 + 14.7) + 52.0438) - (((1.42157E-021 * (D35 + 14.7) ^ 6 - 4.32881E-017 * (D35 + 14.7) ^ 5 + 0.000000000000475377 * (D35 + 14.7) ^ 4 - 0.00000000221001 * (D35 + 14.7) ^ 3 + 0.00000423447 * (D35 + 14.7) ^ 2 - 0.00870071 * (D35 + 14.7) + 52.0438) - (7.84314E-022 * (D35 + 14.7) ^ 6 - 2.65743E-017 * (D35 + 14.7) ^ 5 + 0.000000000000356966 * (D35 + 14.7) ^ 4 - 0.00000000243549 * (D35 + 14.7) ^ 3 + 0.00000907989 * (D35 + 14.7) ^ 2 - 0.0195933 * (D35 + 14.7) + 75.9343)) / (280 - 74)) * (280 - D37)
  const waterInterfacialTension =
    1.42157e-21 * (P + 14.7) ** 6 -
    4.32881e-17 * (P + 14.7) ** 5 +
    0.000000000000475377 * (P + 14.7) ** 4 -
    0.00000000221001 * (P + 14.7) ** 3 +
    0.00000423447 * (P + 14.7) ** 2 -
    0.00870071 * (P + 14.7) +
    52.0438 -
    ((1.42157e-21 * (P + 14.7) ** 6 -
      4.32881e-17 * (P + 14.7) ** 5 +
      0.000000000000475377 * (P + 14.7) ** 4 -
      0.00000000221001 * (P + 14.7) ** 3 +
      0.00000423447 * (P + 14.7) ** 2 -
      0.00870071 * (P + 14.7) +
      52.0438 -
      (7.84314e-22 * (P + 14.7) ** 6 -
        2.65743e-17 * (P + 14.7) ** 5 +
        0.000000000000356966 * (P + 14.7) ** 4 -
        0.00000000243549 * (P + 14.7) ** 3 +
        0.00000907989 * (P + 14.7) ** 2 -
        0.0195933 * (P + 14.7) +
        75.9343)) /
      (280 - 74)) *
      (280 - T);

  // D175: D175;
  const bubbleRiseVelocity =
    2 ** 0.5 *
    ((1.28 * (liquidDensityLBMFT3 - gasDensityLBMFT3)) /
      (liquidDensityLBMFT3 - gasDensityLBMFT3)) **
      0.25;

  // ONLY IN DUNS/ROSS AND ORK
  // D166: =D166*(1/(1+D16))+D167*(D16/(1+D16))
  const liquidInterfacialTension =
    gasOilInterfacialTension * (1 / (1 + waterCutRatio)) +
    waterInterfacialTension * (waterCutRatio / (1 + waterCutRatio));

  const noSlipLiquidHoldupLambdaCL =
    liquidSuperficialVelocityFTSEC / mixSuperficialVelocityFTSEC;

  // ONLY BEGGS BRILL
  // D174::D179
  // FROUDE NUMBER
  // D:174 =((D151^2)/(D67*D132))
  const froudeNumber =
    mixSuperficialVelocityFTSEC ** 2 / (GRAVITY_FT_SEC * (pipeDiameterIn / 12));

  // FROUDE TRANSITION NUMBERS
  // D176: =316*D153^0.302
  const XL1 = 316 * noSlipLiquidHoldupLambdaCL ** 0.302;
  // D177: =0.0009252316*D153^(-2.4684)
  const XL2 = 0.0009252316 * noSlipLiquidHoldupLambdaCL ** -2.46842;
  // D178: =0.1*D153^(-1.4516)
  const XL3 = 0.1 * noSlipLiquidHoldupLambdaCL ** -1.45155;
  // D179: =0.5*D153^(-6.738)
  const XL4 = 0.5 * noSlipLiquidHoldupLambdaCL ** -6.738;

  const froudeTransitionNumbers = [XL1, XL2, XL3, XL4];

  // RN
  // D174: =D149*(D63/(D166*D67))^(1/4)
  const gasVelocityNumber =
    gasSuperficialVelocityFTSEC *
    (liquidDensityLBMFT3 / (liquidInterfacialTension * GRAVITY_FT_SEC)) ** 0.25;

  // N
  // D176: =D150*(D63/(D166*D67))^(1/4)
  const liquidVelocityNumber =
    liquidSuperficialVelocityFTSEC *
    (liquidDensityLBMFT3 / (liquidInterfacialTension * GRAVITY_FT_SEC)) ** 0.25;

  // // N_I
  // // D180: =D130*(D67/(D63*D166^3))^(1/4)
  const liquidViscosityNumber =
    liquidViscosityCP *
    (GRAVITY_FT_SEC / (liquidDensityLBMFT3 * liquidInterfacialTension ** 3)) **
      0.25;

  // D178: =(D132/12)*SQRT((D67*D63)/D166)
  const pipeDiameterNumber =
    (pipeDiameterIn / 12) *
    Math.sqrt(
      (GRAVITY_FT_SEC * liquidDensityLBMFT3) / liquidInterfacialTension
    );

  // =(1488*D63*D150*(D178/12))/D130
  const liquidReynoldsNumber =
    (1488 *
      liquidDensityLBMFT3 *
      liquidSuperficialVelocityFTSEC *
      (pipeDiameterNumber / 12)) /
    liquidViscosityCP;

  // ONLY ORKIZIEWSKI
  // D42: =$PVT.D51/$PVT.D49/($PVT.D18+$PVT.D17)
  // const GLR_f = gasLiquidRatioSCFSTB;

  // Q_g
  // D148: =IFERROR(((D39*D42)/(86400*(1-D16)))*D79,0)
  const inSituGasFlowRateCFS =
    CubicFeetPerSecond.toThisUnit(freeGasFlowRateRCFD);

  // q_ls
  // D146: =D142*0.000064983603391873
  const inSituLiquidFlowRateCFS =
    CubicFeetPerSecond.toThisUnit(liquidFlowRateBBLD);

  // q_t
  // D152: =D149+D146
  const inSituFluidFlowRateCFS =
    CubicFeetPerSecond.toThisUnit(fluidFlowRateBBLD);

  const wettedPipePerimeter = 2 * Math.PI * (pipeDiameterIn / 2 / 12);
  const pipeCrossSectional = Math.PI * (pipeDiameterIn / 2 / 12) ** 2;
  const hydraulicDiameterFt = (4 * pipeCrossSectional) / wettedPipePerimeter;

  // D207: =D154*((D63/(D67*D173))^(1/4))/D138
  const dimensionlessGasVelocity =
    (gasSuperficialVelocityFTSEC *
      (liquidDensityLBMFT3 / (GRAVITY_FT_SEC * liquidInterfacialTension)) **
        0.25) /
    pipeCrossSectional;

  return {
    totalMass,
    gasOilInterfacialTension,
    waterInterfacialTension,
    liquidInterfacialTension,
    noSlipLiquidHoldupLambdaCL,
    bubbleRiseVelocity,
    froudeNumber,
    XL1,
    XL2,
    XL3,
    XL4,
    froudeTransitionNumbers,
    gasVelocityNumber,
    liquidVelocityNumber,
    liquidViscosityNumber,
    pipeDiameterNumber,
    liquidReynoldsNumber,
    inSituGasFlowRateCFS,
    inSituLiquidFlowRateCFS,
    inSituFluidFlowRateCFS,
    wettedPipePerimeter,
    pipeCrossSectional,
    hydraulicDiameterFt,
    dimensionlessGasVelocity
  };
}
